import type { Signal } from '@/shared/types'

// Signal types (signals[].kind) as leadgen sends them, in Swedish. The keys are
// leadgen's signal discovery types and its scoring tiers, and the codes Radar
// contract v2 lists (section 3).
const KIND_LABEL: Record<string, string> = {
  hiring: 'Rekrytering',
  job_posting_surge: 'Många jobbannonser',
  key_hiring: 'Nyckelrekrytering',
  rapid_headcount_growth: 'Snabb personaltillväxt',
  facility_manager_hired: 'Ny fastighetschef',
  office_manager_hired: 'Ny kontorschef',
  workplace_manager_hired: 'Ny arbetsplatsansvarig',
  leadership_change: 'Ny ledning',
  layoffs: 'Uppsägningar',
  expansion: 'Expansion',
  market_expansion: 'Ny marknad',
  office_expansion_announced: 'Större kontor',
  relocation: 'Flytt',
  office_move: 'Kontorsflytt',
  office_relocation_planned: 'Planerad kontorsflytt',
  office_relocation_completed: 'Genomförd kontorsflytt',
  lease_termination: 'Uppsagt hyresavtal',
  service_cancellation: 'Uppsagt serviceavtal',
  building_permit_granted: 'Beviljat bygglov',
  new_construction_project: 'Nytt byggprojekt',
  hospitality_opening: 'Ny restaurang eller hotell',
  funding: 'Finansiering',
  funding_round: 'Finansieringsrunda',
  acquisition: 'Förvärv',
  partnership: 'Partnerskap',
  product_launch: 'Produktlansering',
  new_venture_launched: 'Ny verksamhet',
  sustainability_goal_announced: 'Nytt hållbarhetsmål',
  sustainability_milestone_achieved: 'Uppnått hållbarhetsmål',
  certification_achieved: 'Ny certifiering',
  headcount_decline: 'Minskad personalstyrka',
  office_expansion_completed: 'Genomförd kontorsutökning',
  office_renovation_planned: 'Planerad kontorsrenovering',
  office_renovation_completed: 'Genomförd kontorsrenovering',
  lease_signed: 'Nytt hyresavtal',
  construction_completed: 'Färdigt byggprojekt',
  financial_results: 'Ekonomiskt resultat',
  revenue_milestone: 'Ny omsättningsnivå',
  financial_decline: 'Försämrad ekonomi',
  merger_acquisition: 'Fusion eller förvärv',
  divestiture: 'Avyttring',
  strategic_partnership: 'Strategiskt partnerskap',
  restructuring: 'Omstrukturering',
  rebranding: 'Nytt varumärke',
  digital_transformation: 'Digital omställning',
  regulatory_approval: 'Myndighetsgodkännande',
  award_received: 'Utmärkelse',
  company_anniversary: 'Företagsjubileum',
  service_contract_change: 'Ändrat serviceavtal',
  general_market_news: 'Marknadsnyhet',
  other: 'Övrigt',
}

/** The signal type in Swedish. An unknown key is shown de-underscored with a
 *  capital first letter, so a new type still reads as words; no type reads
 *  "Övrigt". */
export function signalKindLabel(kind: string | null | undefined): string {
  const raw = (kind ?? '').trim()
  const known = KIND_LABEL[raw.toLowerCase().replace(/\s+/g, '_')]
  if (known) return known
  const words = raw.replace(/_+/g, ' ').trim()
  return words ? words.charAt(0).toUpperCase() + words.slice(1) : KIND_LABEL.other
}

export function domainOf(url: string): string {
  try {
    const u = new URL(url)
    return u.hostname.startsWith('www.') ? u.hostname.slice(4) : u.hostname
  } catch {
    return url
  }
}

/** Where a signal came from: the source name, else the link's host, and the
 *  link when it is http(s). null when the signal has neither. */
export function signalSource(s: Pick<Signal, 'source' | 'sourceUrl'>): { site: string; href: string | null } | null {
  const url = (s.sourceUrl ?? '').trim()
  const href = /^https?:\/\//i.test(url) ? url : null
  const site = (s.source ?? '').trim() || (href ? domainOf(href) : '')
  return site ? { site, href } : null
}
